/**
 * @file This file contains all the text strings used throughout the game,
 * including messages, guide content, and UI labels.
 * @module textConfig
 */

/**
 * @typedef {Object} GuideText
 * @property {string} title - The title for the game guide.
 * @property {string} howToPlayTitle - The title for the "How to Play" section.
 * @property {string[]} rules - An array of rules for playing the game.
 * @property {string} biomesTitle - The title for the biomes section.
 * @property {string} biomesDescription - Description for the biomes section.
 * @property {string} pathLengthTitle - The title for the path length section.
 * @property {string} pathLengthDescription - Description for the path length section.
 * @property {string} upgradePathDescription - Description for the upgrade path section. 
 * @property {string} campaignProgressionTitle - Title for the campaign progression section.
 */

/**
 * @typedef {Object} GameMessagesText
 * @property {string} placeOnAnimal - Message when trying to place an item on an animal.
 * @property {string} cellNotEmpty - Message when trying to place an item on a non-empty cell.
 * @property {string} upgraded - Message when an item is upgraded.
 * @property {string} builtZoo - Message when a zoo is built.
 * @property {string} clearedItem - Message when an item is cleared.
 * @property {string} cratedAnimal - Message when an animal is crated.
 * @property {string} nextPiece - Message prompting the player for the next piece.
 * @property {string} animalAppeared - Message when a wild animal appears.
 * @property {string} animalsMoved - Message when animals move.
 * @property {string} gameOverHighScore - Game over message with high score.
 * @property {string} gameOver - Generic game over message.
 * @property {string} gameStart - Message when the game starts.
 * @property {string} gameLoaded - Message when a game is loaded.
 * @property {string} saveFailed - Message when saving fails.
 * @property {string} gameSaved - Message when game is saved.
 * @property {string} campaignComplete - Message when campaign is completed.
 * @property {string} quickSaveDeleted - Message when quick save is deleted.
 * @property {string} campaignSaveDeleted - Message when campaign save is deleted.
 * @property {string} milestoneReward - Message for milestone reward.
 * @property {string} levelAdvanced - Message when level is advanced.
 * @property {string} levelCompleted - Message when level is completed.
 * @property {string} notInCampaign - Message when action is not available in campaign mode.
 */

/**
 * @typedef {Object} PromptsText
 * @property {string} newHighScore - Prompt for a new high score.
 * @property {string} enterInitials - Prompt to enter initials.
 * @property {string} saveButton - Text for the save button.
 */

/**
 * @typedef {Object} UiText
 * @property {string} noHighScores - Message when there are no high scores.
 */

/**
 * The main configuration object for all game text strings.
 * @type {Object}
 * @property {GuideText} guide - Text strings for the game guide.
 * @property {GameMessagesText} gameMessages - Text strings for in-game messages.
 * @property {PromptsText} prompts - Text strings for various prompts.
 * @property {UiText} ui - Text strings for general UI elements.
 */
export const textConfig = {
    guide: {
        title: 'Game Guide',
        howToPlayTitle: 'How to Play',
        rules: [
            'Place items from the queue onto empty squares on the grid.',
            'Create a group of 3 or more identical items to merge them into the next tier.',
            `Every ${4} turns, a wild animal will appear, blocking a square.`, // Using Game.TURNS_PER_ANIMAL directly here
            'Surround an animal on all 4 sides to rescue it into a crate.',
            'Merge 3 or more crates to build a Zoo!',
            'The game ends when the board is full.'
        ],
        biomesTitle: 'Biomes',
        biomesDescription: 'Biomes change the visual appearance and the items you can build. Select a biome below to explore its unique upgrade paths.',
        pathLengthTitle: 'Path Length',
        pathLengthDescription: 'Path length determines the number of tiers in the upgrade path. \'Simple\' is a shorter game, while \'Long\' provides a greater challenge.',
        upgradePathDescription: 'The list below shows the upgrade sequence for the selected options, with the chance of each item appearing next in the queue.',
        campaignProgressionTitle: 'Campaign Progression'
    },
    gameMessages: {
        placeOnAnimal: 'You can\'t place an item on a {ANIMAL_NAME}!',
        cellNotEmpty: 'You can only place an item on an empty cell.',
        upgraded: 'Upgraded to a {ITEM_NAME}!',
        builtZoo: 'You built a {ZOO_NAME}!',
        clearedItem: 'You cleared a {ITEM_NAME}! Great job!',
        cratedAnimal: 'You crated the {ANIMAL_NAME} for rescue!',
        nextPiece: 'Place your next piece.',
        animalAppeared: 'A wild {ANIMAL_NAME} has appeared!',
        animalsMoved: 'The animals are on the move!',
        gameOverHighScore: 'Game Over! Score saved. Final Score: {SCORE}',
        gameOver: 'Game Over! Final Score: {SCORE}',
        gameStart: 'Place your first item!',
        gameLoaded: 'Game loaded. Continue playing!',
        saveFailed: 'Can\'t save a completed game.',
        gameSaved: 'Game Saved!',
        campaignComplete: 'Congratulations! You\'ve completed the campaign!',
        quickSaveDeleted: 'Quick Game save deleted.',
        campaignSaveDeleted: 'Campaign progress deleted.',
        milestoneReward: '+{REWARD} Gold for reaching tier {TIER}!',
        levelAdvanced: 'Level advanced!',
        levelCompleted: 'Level completed!',
        notInCampaign: 'This action is only available in Campaign Mode.'

    },
    prompts: {
        newHighScore: 'New High Score: {SCORE}!',
        enterInitials: 'Enter your initials (3 chars):',
        saveButton: 'Save',
        yesContinue: 'Yes, Continue'
    },
    ui: {
        noHighScores: 'No scores yet!'
    }
};